/**
 * @description
 * Domain filter component for filtering data by email domains.
 * Displays domain breakdown and allows selection/exclusion of domains.
 * Integrates with FilterPanel to provide domain-based filtering.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/DomainFilter.test.tsx
 */

import { useState, useMemo } from 'react';
import { Mail, ChevronDown, ChevronUp, Check, Search, X } from 'lucide-react';
import type { DomainInfo, DomainFilterConfig } from '../../../types/vscode-lens';

interface DomainFilterProps {
  domains: DomainInfo[];
  config: DomainFilterConfig;
  onChange: (config: DomainFilterConfig) => void;
  emailColumns: string[];
  disabled?: boolean;
}

/**
 * Domain filter component
 */
export function DomainFilter({
  domains,
  config,
  onChange,
  emailColumns,
  disabled,
}: DomainFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter domains based on search query
  const filteredDomains = useMemo(() => {
    if (!searchQuery) return domains;
    const query = searchQuery.toLowerCase();
    return domains.filter(d => d.domain.toLowerCase().includes(query));
  }, [domains, searchQuery]);
  
  if (domains.length === 0) {
    return null;
  }
  
  const handleToggleDomain = (domain: string) => {
    const newSelected = new Set(config.selectedDomains);
    const newExcluded = new Set(config.excludedDomains);
    
    if (config.mode === 'include') {
      if (newSelected.has(domain)) {
        newSelected.delete(domain);
      } else {
        newSelected.add(domain);
      }
    } else {
      if (newExcluded.has(domain)) {
        newExcluded.delete(domain);
      } else {
        newExcluded.add(domain);
      }
    }
    
    onChange({
      ...config,
      selectedDomains: newSelected,
      excludedDomains: newExcluded,
    });
  };
  
  const handleModeChange = (mode: 'include' | 'exclude' | 'all') => {
    onChange({
      ...config,
      mode,
      enabled: mode !== 'all',
    });
  };
  
  const handleColumnChange = (column: string) => {
    onChange({
      ...config,
      column,
    });
  };
  
  const handleSelectAll = () => {
    if (config.mode === 'include') {
      onChange({
        ...config,
        selectedDomains: new Set(filteredDomains.map(d => d.domain)),
      });
    } else {
      onChange({
        ...config,
        excludedDomains: new Set(filteredDomains.map(d => d.domain)),
      });
    }
  };
  
  const handleClearAll = () => {
    onChange({
      ...config,
      selectedDomains: new Set(),
      excludedDomains: new Set(),
    });
  };
  
  const activeCount = config.mode === 'include' 
    ? config.selectedDomains.size 
    : config.excludedDomains.size;
  
  return (
    <div className="space-y-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={disabled}
        className="flex items-center justify-between w-full text-sm font-medium hover:text-foreground transition-colors disabled:opacity-50"
      >
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          <span>Domain Filters</span>
          <span className="text-xs text-muted-foreground">
            ({domains.length} domains
            {activeCount > 0 && `, ${activeCount} ${config.mode === 'include' ? 'selected' : 'excluded'}`})
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      
      {isExpanded && (
        <div className="space-y-3 pl-6">
          {/* Email Column Selector */}
          {emailColumns.length > 1 && (
            <div>
              <label className="block text-xs font-medium mb-1">
                Filter Column
              </label>
              <select
                value={config.column}
                onChange={(e) => handleColumnChange(e.target.value)}
                disabled={disabled}
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              >
                {emailColumns.map(col => (
                  <option key={col} value={col}>
                    {col.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Filter Mode */}
          <div>
            <label className="block text-xs font-medium mb-1">
              Filter Mode
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleModeChange('all')}
                disabled={disabled}
                className={`px-3 py-2 text-xs rounded-md border transition-colors ${
                  config.mode === 'all'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-white border-input hover:bg-gray-50'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Show All
              </button>
              <button
                onClick={() => handleModeChange('include')}
                disabled={disabled}
                className={`px-3 py-2 text-xs rounded-md border transition-colors ${
                  config.mode === 'include'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-white border-input hover:bg-gray-50'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Include
              </button>
              <button
                onClick={() => handleModeChange('exclude')}
                disabled={disabled}
                className={`px-3 py-2 text-xs rounded-md border transition-colors ${
                  config.mode === 'exclude'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-white border-input hover:bg-gray-50'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Exclude
              </button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {config.mode === 'all' && 'Show all domains'}
              {config.mode === 'include' && 'Show only selected domains'}
              {config.mode === 'exclude' && 'Hide selected domains'}
            </p>
          </div>
          
          {/* Domain List */}
          {config.mode !== 'all' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium">
                  Domains ({config.mode === 'include' ? 'select to show' : 'select to hide'})
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAll}
                    disabled={disabled}
                    className="text-xs text-primary hover:underline disabled:opacity-50"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleClearAll}
                    disabled={disabled}
                    className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                  >
                    Clear
                  </button>
                </div>
              </div>
              
              {/* Search */}
              {domains.length > 5 && (
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search domains..."
                    disabled={disabled}
                    className="w-full rounded-md border border-input bg-white pl-7 pr-7 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
              
              {/* Domain List */}
              <div className="max-h-60 overflow-y-auto space-y-1 border rounded-md p-2">
                {filteredDomains.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    No domains found
                  </p>
                ) : (
                  filteredDomains.map(({ domain, count, percentage }) => {
                    const isSelected = config.mode === 'include'
                      ? config.selectedDomains.has(domain)
                      : config.excludedDomains.has(domain);
                    
                    return (
                      <button
                        key={domain}
                        onClick={() => handleToggleDomain(domain)}
                        disabled={disabled}
                        className={`w-full flex items-center justify-between px-3 py-2 text-xs rounded-md transition-colors ${
                          isSelected
                            ? 'bg-primary/10 border border-primary'
                            : 'hover:bg-gray-50 border border-transparent'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-primary border-primary' : 'border-input'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                          </div>
                          <span className="font-medium truncate" title={domain}>
                            {domain}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground flex-shrink-0 ml-2">
                          <span>{count}</span>
                          <span className="text-xs">({percentage.toFixed(1)}%)</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */